import * as React from 'react';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type PageTitleProps = {
  title?: string;
  titleComponent?: React.ReactElement;
  sideComponent?: React.ReactElement;
  rightComponent?: React.ReactElement;
  sx?: SxProps<Theme>;
  containerAlignItems?: 'center' | 'flex-start';
};

const PageTitle: React.FC<PageTitleProps> = ({
  title,
  titleComponent,
  sideComponent,
  rightComponent,
  sx,
  containerAlignItems = 'center',
}) => {
  const leftRef = React.useRef<HTMLDivElement>(null);
  const rightRef = React.useRef<HTMLDivElement>(null);

  // Right-aligning the action cluster only reads well while it shares the
  // title's row. Once it wraps onto a row of its own, a right-flush cluster
  // looks detached from the content below it, so flip it to the left edge.
  // CSS has no "did this flex item wrap" selector, hence the measurement.
  const [hasWrapped, setHasWrapped] = React.useState(false);

  React.useEffect(() => {
    const measure = () => {
      const left = leftRef.current;
      const right = rightRef.current;
      if (!left || !right) return;

      setHasWrapped(right.offsetTop > left.offsetTop);
    };

    measure();

    const observer = new ResizeObserver(measure);
    [leftRef.current, rightRef.current, leftRef.current?.parentElement].forEach((node) => {
      if (node) observer.observe(node);
    });
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [rightComponent]);

  return (
    <Box
      display='flex'
      flexDirection='row'
      alignItems={containerAlignItems}
      justifyContent='space-between'
      // The right-hand action cluster of most pages (search field + filter
      // buttons + primary CTA) is wider than the space left over on a 13"
      // laptop. Let it drop onto its own row instead of overflowing the
      // viewport.
      flexWrap='wrap'
      sx={{ rowGap: 1.5, columnGap: 2 }}
    >
      <Box ref={leftRef} display='flex' alignItems='center' sx={{ minWidth: 0 }}>
        {title && (
          <Typography variant='h5' fontWeight='bold' mr={3} noWrap sx={sx}>
            {title}
          </Typography>
        )}
        {titleComponent}

        {sideComponent}
      </Box>

      {rightComponent && (
        <Box
          ref={rightRef}
          sx={{
            display: 'flex',
            alignItems: containerAlignItems,
            justifyContent: hasWrapped ? 'flex-start' : 'flex-end',
            // flexGrow lets the cluster span the full width once it has
            // wrapped; min-content keeps it on the title's row for as long as
            // it genuinely fits.
            flexGrow: 1,
            minWidth: 'min-content',
          }}
        >
          {rightComponent}
        </Box>
      )}
    </Box>
  );
};

export default PageTitle;
